<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from '@/composable/useI18n'
import {
  fromDatetimeLocal,
  nowRounded,
  toDatetimeLocal,
} from '@/utils/date/date-time'

const props = withDefaults(
  defineProps<{
    isOpen: boolean
    initialStart?: Date
    initialEnd?: Date
  }>(),
  {},
)

const emit = defineEmits<{
  close: []
  apply: [start: Date, end: Date]
}>()

const { t } = useI18n()

const customFrom = ref('')
const customTo = ref('')

watch(
  () => props.isOpen,
  (open) => {
    if (!open) return

    const end = props.initialEnd ?? nowRounded()
    const start = props.initialStart ?? new Date(end.getTime() - 24 * 60 * 60 * 1000)
    customFrom.value = toDatetimeLocal(start)
    customTo.value = toDatetimeLocal(end)
  },
)

function applyCustom() {
  const start = fromDatetimeLocal(customFrom.value)
  const end = fromDatetimeLocal(customTo.value)
  if (start >= end) return
  emit('apply', start, end)
}

function cancel() {
  emit('close')
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[100001]"
      @click.self="cancel"
    >
      <div
        class="absolute left-1/2 top-1/2 z-[100002] w-full max-w-md -translate-x-1/2 -translate-y-1/2 border border-stroke bg-white p-5 shadow-xl"
      >
        <h4 class="mb-3 text-sm font-semibold text-black">
          {{ t('time_range_filter.custom_title') }}
        </h4>

        <div class="space-y-3">
          <div>
            <label class="mb-1 block text-xs font-bold uppercase tracking-wider text-body">
              {{ t('time_range_filter.from') }}
            </label>
            <input
              v-model="customFrom"
              type="datetime-local"
              class="w-full border border-stroke bg-gray-2 px-3 py-2.5 text-sm text-black outline-none focus:border-primary"
            />
          </div>
          <div>
            <label class="mb-1 block text-xs font-bold uppercase tracking-wider text-body">
              {{ t('time_range_filter.to') }}
            </label>
            <input
              v-model="customTo"
              type="datetime-local"
              class="w-full border border-stroke bg-gray-2 px-3 py-2.5 text-sm text-black outline-none focus:border-primary"
            />
          </div>
        </div>

        <div class="mt-4 flex justify-end gap-2">
          <button
            type="button"
            class="px-4 py-2 text-xs font-medium uppercase tracking-wide text-body hover:text-black"
            @click="cancel"
          >
            {{ t('time_range_filter.cancel') }}
          </button>
          <button
            type="button"
            class="bg-blue px-4 py-2 text-xs font-medium uppercase tracking-wide text-white hover:bg-blue-600"
            @click="applyCustom"
          >
            {{ t('time_range_filter.apply') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
